import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { compressImage } from '../lib/imageUtils';

export default function LoginPage() {
    const navigate = useNavigate();
    const [isSignUp, setIsSignUp] = useState(false);
    const [step, setStep] = useState(1);

    // Form fields
    const [restaurantName, setRestaurantName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    
    // Operations & Logistics
    const [address, setAddress] = useState('');
    const [tableCount, setTableCount] = useState(1);
    const [businessType, setBusinessType] = useState('Cafe');

    // Identity & Preferences
    const [paymentPreference, setPaymentPreference] = useState('Cash + Online');
    const [email, setEmail] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [tagline, setTagline] = useState('');
    const [cuisineType, setCuisineType] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [instagram, setInstagram] = useState('');

    const [showPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [otp, setOtp] = useState('');
    const [, setIsOtpSent] = useState(false);
    const [isRecovery, setIsRecovery] = useState(false);
    const [recoveryStep, setRecoveryStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        supabase.auth.getUser().then(({ data: { user } }) => {
            if (user) navigate('/dashboard', { replace: true });
        });
    }, [navigate]);

    const handleLogoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { setError('Logo must be under 2MB'); return; }
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const validateStep1 = () => {
        if (!restaurantName.trim()) return 'Restaurant name is required';
        if (!phone.trim() || phone.length !== 10) return 'Valid 10-digit phone number is required';
        if (password.length < 6) return 'Password must be at least 6 characters';
        return null;
    };

    const validateStep2 = () => {
        if (!address.trim()) return 'City & Address is required';
        if (!tableCount || tableCount < 1) return 'Number of tables must be at least 1';
        return null;
    };

    const validateStep3 = () => {
        if (!paymentPreference) return 'Payment preference is required';
        if (!email.trim()) return 'Email is required';
        return null;
    };

    const nextStep = () => {
        const err = step === 1 ? validateStep1() : step === 2 ? validateStep2() : null;
        if (err) { setError(err); return; }
        setError('');
        setStep(prev => prev + 1);
    };

    const prevStep = () => {
        setError('');
        setStep(prev => prev - 1);
    };

    async function handleSubmit(e) {
        if (e) e.preventDefault();
        
        // If we are at Step 1 or 2, just go next
        if (isSignUp && step < 3) {
            nextStep();
            return;
        }

        const finalErr = isSignUp ? validateStep3() : (email && password ? null : 'Email and Password required');
        if (finalErr) { setError(finalErr); return; }

        setLoading(true);
        setError('');

        try {
            if (isSignUp) {
                const finalEmail = email.trim();
                const { error: signUpErr } = await supabase.auth.signUp({ 
                    email: finalEmail, 
                    password,
                    options: {
                        data: {
                            restaurant_name: restaurantName,
                            phone: phone
                        }
                    }
                });

                if (signUpErr) {
                    if (signUpErr.message?.toLowerCase().includes('already registered')) {
                        // If they are already registered, maybe they just need to verify?
                        // Or they are trying to sign in.
                        const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email: finalEmail, password });
                        if (signInErr) throw new Error('Email already registered. Please login or check your password.');
                        
                        const userId = signInData?.user?.id;
                        const { data: existingRest } = await supabase.from('restaurants').select('id').eq('owner_id', userId).single();
                        if (existingRest) { navigate('/dashboard', { replace: true }); return; }
                        
                        // If they are logged in but have no restaurant, they probably need to finish profile setup
                        // But for simplicity in this flow, we'll assume they need to verify first if it's a new signup
                    } else {
                        throw signUpErr;
                    }
                }

                // If signup worked, we show the OTP step
                setIsOtpSent(true);
                setStep(4);
                setLoading(false);
                return;
            } else {
                // LOGIN FLOW
                const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
                if (signInErr) throw signInErr;
                
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const { data: rest } = await supabase.from('restaurants').select('plan').eq('owner_id', user.id).single();
                    if (rest && !rest.plan) { navigate('/subscription', { replace: true }); return; }
                }
                navigate('/dashboard', { replace: true });
            }
        } catch (err) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    }

    async function handleVerifyOtp(e) {
        if (e) e.preventDefault();
        if (!otp || otp.length !== 6) { setError('Please enter 6-digit OTP'); return; }

        setLoading(true);
        setError('');

        try {
            // 1. Verify OTP with Supabase
            const { data: { session }, error: verifyErr } = await supabase.auth.verifyOtp({
                email: email.trim(),
                token: otp,
                type: 'signup'
            });

            if (verifyErr) throw verifyErr;
            if (!session?.user) throw new Error('Verification failed. Please try again.');

            const userId = session.user.id;

            // 2. Logo Upload (if any)
            let logoUrl = null;
            if (logoFile) {
                try {
                    const compressed = await compressImage(logoFile, { maxWidthOrHeight: 800 });
                    const fileName = `logos/${userId}-${Date.now()}.jpg`;
                    const { error: upErr } = await supabase.storage.from('menu-images').upload(fileName, compressed, { upsert: true });
                    if (!upErr) {
                        const { data: urlData } = supabase.storage.from('menu-images').getPublicUrl(fileName);
                        logoUrl = urlData?.publicUrl || null;
                    }
                } catch (_) {}
            }

            // 3. Create Restaurant Profile
            const restaurantPayload = {
                owner_id: userId,
                name: restaurantName.trim(),
                phone: phone.trim(),
                address: address.trim(),
                table_count: parseInt(tableCount) || 1,
                business_type: businessType,
                payment_preference: paymentPreference,
                email: email.trim(),
                logo_url: logoUrl,
                tagline: tagline.trim(),
                cuisine_type: cuisineType.trim(),
                tax_id: gstNumber.trim(),
                social_links: { instagram: instagram.trim() },
                currency: 'INR',
                is_accepting_orders: true,
                plan: null,
            };

            const { error: insertErr } = await supabase.from('restaurants').insert([restaurantPayload]);
            if (insertErr) throw insertErr;

            navigate('/subscription', { replace: true });
        } catch (err) {
            setError(err.message || 'OTP verification failed');
            setLoading(false);
        }
    }

    async function handleForgotPassword(e) {
        if (e) e.preventDefault();
        if (!email.trim()) { setError('Please enter your email'); return; }

        setLoading(true);
        setError('');

        try {
            const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim());
            if (resetErr) throw resetErr;
            setRecoveryStep(2);
        } catch (err) {
            setError(err.message || 'Failed to send recovery code');
        } finally {
            setLoading(false);
        }
    }

    async function handleVerifyRecoveryOtp(e) {
        if (e) e.preventDefault();
        if (!otp || otp.length !== 6) { setError('Please enter 6-digit OTP'); return; }

        setLoading(true);
        setError('');

        try {
            const { error: verifyErr } = await supabase.auth.verifyOtp({
                email: email.trim(),
                token: otp,
                type: 'recovery'
            });
            if (verifyErr) throw verifyErr;
            setRecoveryStep(3);
        } catch (err) {
            setError(err.message || 'OTP verification failed');
        } finally {
            setLoading(false);
        }
    }

    async function handleUpdatePassword(e) {
        if (e) e.preventDefault();
        if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }

        setLoading(true);
        setError('');

        try {
            const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
            if (updateErr) throw updateErr;
            setIsRecovery(false);
            setRecoveryStep(1);
            setIsSignUp(false);
            setSuccessMsg('Password updated successfully. Please login.');
        } catch (err) {
            setError(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    }

    const slideVariants = {
        enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction) => ({ x: direction < 0 ? 50 : -50, opacity: 0 })
    };

    return (
        <div className="min-h-screen bg-[#FDFCF7] flex font-display">
            {/* Left Content — Fixed visibility */}
            <div className="hidden lg:flex lg:w-[45%] relative bg-[#1A1A1A] text-white p-16 flex-col justify-between overflow-hidden">
                {/* Decorative background element */}
                <div className="absolute top-[-10%] right-[-10%] w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px]" />
                
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-12">
                        <img src="/logo.png" alt="GetYourQR Logo" className="w-10 h-10 object-contain rounded-xl bg-white" />
                        <span className="text-xl font-bold tracking-tight uppercase">GetYourQR</span>
                    </div>
                    
                    <h2 className="text-5xl font-black leading-[1.1] mb-6">
                        Digitize your menu <br/>
                        <span className="text-primary italic">in 3 minutes.</span>
                    </h2>
                    <p className="text-white/60 text-lg max-w-sm leading-relaxed mb-12">
                        The world's most intuitive dashboard for modern restaurant owners.
                    </p>
                </div>

                <div className="relative z-10 grid grid-cols-2 gap-8">
                    {[
                        { val: '2k+', label: 'Restaurants' },
                        { val: '1M+', label: 'Orders Processed' }
                    ].map(stat => (
                        <div key={stat.label}>
                            <div className="text-3xl font-black text-primary">{stat.val}</div>
                            <div className="text-sm text-white/40 font-bold uppercase tracking-widest mt-1">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Content — The Wizard */}
            <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12">
                <div className="w-full max-w-[480px]">
                    
                    {/* Header */}
                    <div className="mb-10 text-center lg:text-left">
                        <div className="lg:hidden flex justify-center mb-6">
                            <span className="text-3xl font-black uppercase italic tracking-tighter">
                                GetYour<span className="text-primary">QR</span>
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                            {isRecovery ? 'Recover Password' : !isSignUp ? 'Welcome Back' : step === 1 ? 'Start Your Journey' : step === 2 ? 'Customize Your Brand' : step === 3 ? 'Almost There!' : 'Verify Email'}
                        </h1>
                        <p className="text-slate-500 mt-2">
                            {isRecovery ? (recoveryStep === 2 ? `Enter code sent to ${email}` : 'Reset your password') : isSignUp ? (step === 4 ? `Enter the code sent to ${email}` : `Phase ${step} of 3`) : 'Log in to manage your digital ecosystem.'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="relative min-h-[400px]">
                        <AnimatePresence mode="wait" initial={false}>
                            {isRecovery ? (
                                <motion.div key="recovery" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                    {recoveryStep === 1 && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Registered Email</label>
                                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="owner@restaurant.com" />
                                            </div>
                                            <button type="button" onClick={handleForgotPassword} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20" disabled={loading}>
                                                {loading ? 'Sending Code...' : 'Send Recovery Code'}
                                            </button>
                                            <button type="button" onClick={() => setIsRecovery(false)} className="w-full text-[10px] font-black text-slate-400 uppercase tracking-widest">Back to Login</button>
                                        </div>
                                    )}
                                    {recoveryStep === 2 && (
                                        <div className="space-y-4">
                                            <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex gap-3 text-left mb-6">
                                                <span className="material-symbols-outlined text-amber-600">mail</span>
                                                <p className="text-xs font-bold text-amber-800 leading-relaxed">
                                                    Enter the 6-digit recovery code sent to your email.
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-center">Recovery Code</label>
                                                <input type="text" maxLength="6" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} className="w-full bg-white border-2 border-slate-100 h-16 px-5 rounded-2xl focus:border-primary transition-colors font-black text-2xl text-center tracking-[0.5em]" placeholder="000000" />
                                            </div>
                                            <button type="button" onClick={handleVerifyRecoveryOtp} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20" disabled={loading}>
                                                {loading ? 'Verifying...' : 'Verify Code'}
                                            </button>
                                        </div>
                                    )}
                                    {recoveryStep === 3 && (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">New Password</label>
                                                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="At least 6 characters" />
                                            </div>
                                            <button type="button" onClick={handleUpdatePassword} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20" disabled={loading}>
                                                {loading ? 'Update Password' : 'Update Password'}
                                            </button>
                                        </div>
                                    )}
                                </motion.div>
                            ) : !isSignUp ? (
                                <motion.div key="login" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Email Address</label>
                                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary focus:ring-0 transition-colors font-bold placeholder:text-slate-300" placeholder="owner@restaurant.com" />
                                        </div>
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400">Password</label>
                                                <button type="button" onClick={() => setIsRecovery(true)} className="text-[10px] font-black text-primary uppercase tracking-widest hover:underline">Forgot?</button>
                                            </div>
                                            <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary focus:ring-0 transition-colors font-bold placeholder:text-slate-300" placeholder="••••••••" />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <button type="submit" disabled={loading} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-colors disabled:opacity-50">
                                            {loading ? 'Authenticating...' : 'Sign In'}
                                        </button>
                                        <button type="button" onClick={() => setIsRecovery(true)} className="w-full h-14 rounded-2xl font-black uppercase tracking-widest text-xs text-slate-400 hover:text-primary transition-colors border-2 border-transparent hover:border-slate-100">
                                            Forgot Password? Recover via OTP
                                        </button>
                                    </div>
                                </motion.div>
                            ) : (
                                <>
                                    {step === 1 && (
                                        <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Restaurant Name *</label>
                                                    <input type="text" value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="e.g. Punjabi Tadka" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Phone Number *</label>
                                                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="10 Digits" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Password *</label>
                                                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="••••••••" />
                                                </div>
                                            </div>
                                            <button type="button" onClick={nextStep} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] transition-colors">Next: Operations</button>
                                        </motion.div>
                                    )}

                                    {step === 2 && (
                                        <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">City & Address *</label>
                                                    <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="e.g. MG Road, Mumbai" />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Number of Tables *</label>
                                                        <input type="number" min="1" value={tableCount} onChange={(e) => setTableCount(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold" placeholder="e.g. 10" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Business Type *</label>
                                                        <select value={businessType} onChange={(e) => setBusinessType(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold">
                                                            <option>Cafe</option>
                                                            <option>Fine Dine</option>
                                                            <option>QSR</option>
                                                            <option>Bar / Pub</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-4">
                                                <button type="button" onClick={prevStep} className="flex-1 bg-slate-100 h-14 rounded-2xl font-black uppercase tracking-widest text-xs text-slate-500">Back</button>
                                                <button type="button" onClick={nextStep} className="flex-[2] bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] transition-colors">Next: Preferences</button>
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 3 && (
                                        <motion.div key="step3" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                            <div className="space-y-4 max-h-[380px] overflow-y-auto px-1 scrollbar-hide">
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Payment Preference *</label>
                                                    <select value={paymentPreference} onChange={(e) => setPaymentPreference(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold">
                                                        <option>Cash + Online</option>
                                                        <option>Online Payments Only</option>
                                                        <option>Pay at Counter</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1 font-medium text-left">Email *</label>
                                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold text-sm" placeholder="e.g. admin@restaurant.com" required />
                                                </div>
                                                <div className="flex items-center gap-4 py-3 border-b border-slate-50">
                                                    <button type="button" onClick={() => document.getElementById('reg-logo').click()} className="w-16 h-16 rounded-2xl bg-white border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors shrink-0">
                                                        {logoPreview ? <img src={logoPreview} className="w-full h-full object-cover" alt="logo" /> : <span className="text-[10px] text-slate-300 font-bold">LOGO</span>}
                                                    </button>
                                                    <div className="flex-1 text-left">
                                                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Restaurant Logo (Optional)</label>
                                                        <button type="button" onClick={() => document.getElementById('reg-logo').click()} className="text-[10px] font-black text-primary uppercase underline">Choose File</button>
                                                        <input id="reg-logo" type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Brand Tagline (Optional)</label>
                                                    <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold text-sm" placeholder="e.g. Best Pizza in Town" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Main Cuisine (Optional)</label>
                                                    <input type="text" value={cuisineType} onChange={(e) => setCuisineType(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold text-sm" placeholder="e.g. North Indian" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">GST Number (Optional)</label>
                                                    <input type="text" value={gstNumber} onChange={(e) => setGstNumber(e.target.value.toUpperCase())} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold uppercase text-sm" placeholder="GSTIN" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-left">Instagram @Handle (Optional)</label>
                                                    <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} className="w-full bg-white border-2 border-slate-100 h-14 px-5 rounded-2xl focus:border-primary transition-colors font-bold text-sm" placeholder="e.g. your_cafe" />
                                                </div>
                                            </div>
                                            
                                            <p className="text-[10px] text-slate-400 text-center mt-4">Currency set to <span className="font-bold text-slate-600">Indian Rupees (INR)</span></p>

                                            <div className="flex gap-4">
                                                <button type="button" onClick={prevStep} className="flex-1 bg-slate-100 h-14 rounded-2xl font-black uppercase tracking-widest text-xs text-slate-500">Back</button>
                                                <button type="button" onClick={handleSubmit} disabled={loading} className="flex-[2] bg-[#1A1A1A] text-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-black/10">
                                                    {loading ? 'Setting up...' : 'Create My Account'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}

                                    {step === 4 && (
                                        <motion.div key="step4" variants={slideVariants} initial="enter" animate="center" exit="exit" className="space-y-6">
                                            <div className="space-y-4">
                                                <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex gap-3 text-left">
                                                    <span className="material-symbols-outlined text-amber-600">mail</span>
                                                    <p className="text-xs font-bold text-amber-800 leading-relaxed">
                                                        We've sent a 6-digit code to <span className="text-amber-900">{email}</span>. Please enter it below to verify your account.
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 text-center">Verification Code</label>
                                                    <input 
                                                        type="text" 
                                                        maxLength="6"
                                                        value={otp} 
                                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} 
                                                        className="w-full bg-white border-2 border-slate-100 h-16 px-5 rounded-2xl focus:border-primary transition-colors font-black text-2xl text-center tracking-[0.5em]" 
                                                        placeholder="000000" 
                                                    />
                                                </div>
                                                <button 
                                                    type="button" 
                                                    onClick={() => { setIsOtpSent(false); setStep(3); }}
                                                    className="w-full text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-primary transition-colors"
                                                >
                                                    Wait, I entered the wrong email?
                                                </button>
                                            </div>
                                            <div className="flex gap-4">
                                                <button type="button" onClick={handleVerifyOtp} className="w-full bg-primary h-14 rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] transition-colors" disabled={loading}>
                                                    {loading ? 'Verifying...' : 'Verify & Continue'}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </>
                            )}
                        </AnimatePresence>

                    </form>

                    {/* Footer Toggle */}
                    {(step === 1 || !isSignUp) && !isRecovery ? (
                        <div className="mt-12 pt-8 border-t border-slate-100">
                            <p className="text-center text-sm font-bold text-slate-400">
                                {!isSignUp ? "Don't have an account? " : "Already an owner? "}
                                <button onClick={() => { setIsSignUp(!isSignUp); setStep(1); setError(''); setSuccessMsg(''); setIsRecovery(false); }} className="text-slate-900 font-black hover:underline underline-offset-4 ml-1">
                                    {!isSignUp ? 'REGISTER RESTAURANT / CAFE' : 'BACK TO LOGIN'}
                                </button>
                            </p>
                        </div>
                    ) : null}

                    {/* Global Error Display */}
                    {error && (
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mt-8 p-4 bg-red-50 rounded-2xl border border-red-100">
                            <p className="text-red-500 text-xs font-bold text-center leading-relaxed italic">{error}</p>
                        </motion.div>
                    )}

                    {/* Global Success Display */}
                    {successMsg && (
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mt-8 p-4 bg-green-50 rounded-2xl border border-green-100">
                            <p className="text-green-700 text-xs font-bold text-center leading-relaxed">{successMsg}</p>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
}

